import { useState } from "react";
import { 
  MoreHorizontal, 
  CheckCheck, 
  Archive, 
  Clock, 
  Trash2,
  Pin,
  BellOff,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface NotificationQuickActionsProps {
  notificationId: string;
  isRead: boolean;
  isArchived?: boolean;
  isPinned?: boolean;
  actionUrl?: string;
  onMarkAsRead?: () => void;
  onArchive?: () => void;
  onSnooze?: () => void;
  onDelete?: () => void;
  onPin?: () => void;
  onMute?: () => void;
  className?: string;
}

export function NotificationQuickActions({
  notificationId,
  isRead,
  isArchived,
  isPinned,
  actionUrl,
  onMarkAsRead,
  onArchive,
  onSnooze,
  onDelete,
  onPin,
  onMute,
  className,
}: NotificationQuickActionsProps) {
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity",
            "focus:opacity-100",
            open && "opacity-100",
            className
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Open notification actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {!isRead && onMarkAsRead && (
          <DropdownMenuItem onClick={onMarkAsRead}>
            <CheckCheck className="h-4 w-4 mr-2" />
            Mark as read
          </DropdownMenuItem>
        )}
        
        {onArchive && (
          <DropdownMenuItem onClick={onArchive}>
            <Archive className="h-4 w-4 mr-2" />
            {isArchived ? "Unarchive" : "Archive"}
          </DropdownMenuItem>
        )}
        
        {onSnooze && (
          <DropdownMenuItem onClick={onSnooze}>
            <Clock className="h-4 w-4 mr-2" />
            Snooze
          </DropdownMenuItem>
        )}
        
        {onPin && (
          <DropdownMenuItem onClick={onPin}>
            <Pin className="h-4 w-4 mr-2" />
            {isPinned ? "Unpin" : "Pin to top"}
          </DropdownMenuItem>
        )}

        {actionUrl && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <a href={actionUrl} className="flex items-center">
                <ExternalLink className="h-4 w-4 mr-2" />
                View details
              </a>
            </DropdownMenuItem>
          </>
        )}
        
        {onMute && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onMute} className="text-muted-foreground">
              <BellOff className="h-4 w-4 mr-2" />
              Mute this type
            </DropdownMenuItem>
          </>
        )}
        
        {onDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={onDelete}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
